import { Card } from '@/components/ui/card';
import {
  Activity, AlertOctagon, AlertTriangle, Bell, DollarSign,
  TrendingUp, TrendingDown, Percent, Scissors, Boxes, Receipt, Store
} from 'lucide-react';

function fmt(n) {
  if (n == null) return '$0';
  return '$' + Number(n).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function SeverityBadge({ severity }) {
  const map = {
    high: { cls: 'bg-red-500/10 text-red-400 border-red-900/50', icon: AlertOctagon, label: 'Crítico' },
    medium: { cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-900/50', icon: AlertTriangle, label: 'Atención' },
    low: { cls: 'bg-blue-500/10 text-blue-400 border-blue-900/50', icon: Bell, label: 'Info' },
  };
  const cfg = map[severity] || map.low;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

export default function UtilitySection({ utility }) {
  const totals = utility?.totals || {};
  const byBranch = utility?.by_branch || [];
  const alerts = utility?.alerts || [];

  const utilityColor = (v) => v < 0 ? 'text-red-400' : v < 1000 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className="space-y-4">
      {/* Totals header */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="utility-totals-card">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-gold" />
          <h2 className="text-sm font-semibold text-white">Utilidad Operativa Controlable — Mes Actual</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-zinc-800/30 rounded-sm p-3" data-testid="util-revenue">
            <div className="flex items-center gap-1.5 mb-1">
              <DollarSign className="w-3.5 h-3.5 text-green-500" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Ventas</span>
            </div>
            <p className="text-lg font-bold text-white">{fmt(totals.revenue)}</p>
          </div>
          <div className="bg-zinc-800/30 rounded-sm p-3" data-testid="util-commissions">
            <div className="flex items-center gap-1.5 mb-1">
              <Scissors className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">− Comisiones</span>
            </div>
            <p className="text-lg font-bold text-blue-400">{fmt(totals.commissions)}</p>
          </div>
          <div className="bg-zinc-800/30 rounded-sm p-3" data-testid="util-supplies">
            <div className="flex items-center gap-1.5 mb-1">
              <Boxes className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">− Insumos</span>
            </div>
            <p className="text-lg font-bold text-purple-400">{fmt(totals.supplies_cost)}</p>
          </div>
          <div className="bg-zinc-800/30 rounded-sm p-3" data-testid="util-fixed">
            <div className="flex items-center gap-1.5 mb-1">
              <Receipt className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider">− Gastos Fijos</span>
            </div>
            <p className="text-lg font-bold text-orange-400">{fmt(totals.fixed_expenses)}</p>
          </div>
          <div className="bg-gold/10 border border-gold/30 rounded-sm p-3" data-testid="util-utility">
            <div className="flex items-center gap-1.5 mb-1">
              {totals.utility >= 0 ? (
                <TrendingUp className="w-3.5 h-3.5 text-gold" />
              ) : (
                <TrendingDown className="w-3.5 h-3.5 text-red-400" />
              )}
              <span className="text-[10px] text-gold uppercase tracking-wider">= Utilidad</span>
            </div>
            <p className={`text-lg font-bold ${utilityColor(totals.utility)}`}>{fmt(totals.utility)}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1">
              <Percent className="w-2.5 h-2.5" />
              Margen {totals.margin_pct || 0}%
            </p>
          </div>
        </div>
        <p className="text-[10px] text-zinc-600 mt-3 italic">
          Fórmula: Ventas − Comisiones − Insumos consumidos − Gastos fijos prorrateados
        </p>
      </Card>

      {/* Alerts */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="alerts-card">
        <h3 className="text-sm text-zinc-400 mb-3 flex items-center gap-2">
          <Bell className="w-4 h-4 text-gold" />
          Alertas y Notificaciones ({alerts.length})
        </h3>
        {alerts.length === 0 ? (
          <div className="text-center py-6">
            <div className="inline-flex w-12 h-12 rounded-full bg-green-500/10 items-center justify-center mb-2">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <p className="text-zinc-400 text-sm">Todo en orden en la zona</p>
            <p className="text-zinc-600 text-xs">No hay alertas activas</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {alerts.map((a, i) => (
              <div
                key={`${a.type}-${a.branch_id || 'zone'}-${i}`}
                className="flex items-start gap-3 bg-zinc-800/30 rounded-sm p-3 border-l-2 border-l-zinc-700 hover:border-l-gold transition-colors"
                data-testid={`alert-${a.type}`}
              >
                <SeverityBadge severity={a.severity} />
                <div className="flex-1">
                  <p className="text-sm text-zinc-200">{a.message}</p>
                  {a.branch_name && (
                    <p className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1">
                      <Store className="w-2.5 h-2.5" />
                      {a.branch_name}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Per-branch breakdown */}
      <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="utility-by-branch-card">
        <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
          <Store className="w-4 h-4" />
          Utilidad por Sucursal
        </h3>
        {byBranch.length === 0 ? (
          <p className="text-zinc-500 text-sm text-center py-4">No hay sucursales en la zona</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left text-zinc-500 py-2 px-3 font-medium">Sucursal</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Ventas</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Comisiones</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Insumos</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Gastos</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Utilidad</th>
                  <th className="text-right text-zinc-500 py-2 px-3 font-medium">Margen</th>
                </tr>
              </thead>
              <tbody>
                {byBranch.map((b) => (
                  <tr key={b.branch_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`util-row-${b.branch_id}`}>
                    <td className="py-3 px-3 text-zinc-200">{b.branch_name}</td>
                    <td className="text-right py-3 px-3 text-white">{fmt(b.revenue)}</td>
                    <td className="text-right py-3 px-3 text-blue-400">{fmt(b.commissions)}</td>
                    <td className="text-right py-3 px-3 text-purple-400">{fmt(b.supplies_cost)}</td>
                    <td className="text-right py-3 px-3 text-orange-400">{fmt(b.fixed_expenses)}</td>
                    <td className={`text-right py-3 px-3 font-semibold ${utilityColor(b.utility)}`}>{fmt(b.utility)}</td>
                    <td className={`text-right py-3 px-3 ${b.margin_pct < 0 ? 'text-red-400' : b.margin_pct < 10 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {b.margin_pct}%
                    </td>
                  </tr>
                ))}
              </tbody>
              {byBranch.length > 1 && (
                <tfoot>
                  <tr className="border-t border-zinc-700 bg-zinc-800/30">
                    <td className="py-3 px-3 text-zinc-300 font-semibold">Total Zona</td>
                    <td className="text-right py-3 px-3 text-white font-semibold">{fmt(totals.revenue)}</td>
                    <td className="text-right py-3 px-3 text-blue-400 font-semibold">{fmt(totals.commissions)}</td>
                    <td className="text-right py-3 px-3 text-purple-400 font-semibold">{fmt(totals.supplies_cost)}</td>
                    <td className="text-right py-3 px-3 text-orange-400 font-semibold">{fmt(totals.fixed_expenses)}</td>
                    <td className={`text-right py-3 px-3 font-bold ${utilityColor(totals.utility)}`}>{fmt(totals.utility)}</td>
                    <td className={`text-right py-3 px-3 font-semibold ${totals.margin_pct < 0 ? 'text-red-400' : totals.margin_pct < 10 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {totals.margin_pct || 0}%
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
